import { useMemo } from 'react';
import { LatLngBounds, LatLngBoundsLiteral } from 'leaflet';
import { MapContainer, TileLayer } from 'react-leaflet';
import { RouteMap } from '@lp/features/RoutePlanner/components/RouteMap/RouteMap';
import { getDonorHospitalMarker, getTxCenterMarker } from '@lp/features/RoutePlanner/utils';
import { HospitalMarkerType, TxCenterMarkerType } from '@lp/features/RoutePlanner/types';
import { MapMarkers } from '@lp/features/RoutePlanner/components/RouteMap/MapMarkers';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { HERE_MAPS_TILES_URL } from '@/constants/env.constants';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  RouteLocationType,
} from '@/api/apis/route';
import { CaseFacility, CaseResourceType, OrganType } from '@/api/apis/case';

type CaseLogisticsMapProps = {
  donorHospital?: CaseFacility;
  txCenter?: CaseFacility;
  legs: (AviaLegTypes | GroundLegTypes)[];
  organType: OrganType;
  isRouteDone: boolean;
  locations: RouteLocationType[];
};

const interactionOptions = {
  control: false,
  doubleClickZoom: false,
  closePopupOnClick: false,
  dragging: false,
  trackResize: false,
  touchZoom: false,
  scrollWheelZoom: false,
  keyBoard: false,
  zoomControl: false,
  attributionControl: false,
};

export const CaseLogisticsMap = ({
  legs,
  locations,
  organType,
  isRouteDone,
  donorHospital,
  txCenter,
}: CaseLogisticsMapProps) => {
  const { Aircraft: aircraftsList } = useRouteResources();
  const donorHospitalMarker = useMemo<HospitalMarkerType | null>(
    () => (donorHospital ? getDonorHospitalMarker(donorHospital) : null),
    [donorHospital],
  );
  const txCenterMarker = useMemo<TxCenterMarkerType | null>(
    () => (txCenter ? getTxCenterMarker(txCenter) : null),
    [txCenter],
  );

  const latLngBounds = useMemo(() => {
    if (!isRouteDone) {
      return new LatLngBounds([
        ...(donorHospital ? [[donorHospital.location.latitude, donorHospital.location.longitude]] : []),
        ...(txCenter ? [[txCenter.location.latitude, txCenter.location.longitude]] : []),
      ] as LatLngBoundsLiteral).pad(0.1);
    }

    return undefined;
  }, [isRouteDone, donorHospital, txCenter]);

  const markersList = useMemo(() => {
    return [...(donorHospitalMarker ? [donorHospitalMarker] : []), ...(txCenterMarker ? [txCenterMarker] : [])];
  }, [donorHospitalMarker, txCenterMarker]);

  const aircrafts = useMemo(() => {
    return aircraftsList.map<BaseLegResourceType<AircraftLegResource>>((a) => ({
      id: a.resourceId,
      type: CaseResourceType.Aircraft,
      data: a.data,
    }));
  }, [aircraftsList]);

  return (
    <MapContainer
      center={donorHospitalMarker?.location || txCenterMarker?.location}
      bounds={latLngBounds}
      className="w-full h-full"
      {...interactionOptions}
    >
      <TileLayer url={HERE_MAPS_TILES_URL} />
      {isRouteDone ? (
        <RouteMap
          donorHospitalMarker={donorHospitalMarker}
          txCenterMarker={txCenterMarker}
          organType={organType}
          legs={legs}
          aircrafts={aircrafts}
          locations={locations}
        />
      ) : (
        <MapMarkers markers={markersList} organType={organType} />
      )}
    </MapContainer>
  );
};
